import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { MODULES, moduleForPath, type ModuleKey } from '@/lib/access';
import type { UserRole } from '@/types/models';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { session, loading, profile } = useAuthStore();
  const location = useLocation();

  if (loading) {
    return (
      <div className="centered">
        <span className="spinner" />
        <span className="small">Loading…</span>
      </div>
    );
  }
  if (!session) return <Navigate to="/login" state={{ from: location }} replace />;

  // Deactivated by an administrator. The server already refuses every
  // write from an inactive account; this stops the screens pretending
  // otherwise.
  if (profile && profile.is_active === false) {
    return (
      <div className="centered" style={{ flexDirection: 'column', gap: 'var(--sp-3)', padding: 'var(--sp-4)' }}>
        <h2 style={{ margin: 0 }}>This account has been deactivated</h2>
        <p className="muted" style={{ maxWidth: 460, textAlign: 'center', margin: 0 }}>
          An administrator has switched this account off, so nothing can be captured under it.
          Ask the quarry office if you think this is a mistake.
        </p>
        <button className="btn btn--secondary" onClick={() => void useAuthStore.getState().signOut()}>
          Sign out
        </button>
      </div>
    );
  }

  // An account created with a password an administrator typed is an account
  // whose diary entries that administrator could have written. Until it has
  // been changed, the only screen this user gets is the one that changes it.
  if (profile?.must_change_password && location.pathname !== '/set-password') {
    return <Navigate to="/set-password" replace />;
  }

  return <>{children}</>;
}

/** Route-level role gate. The server's RLS policies are the real boundary;
 *  this only keeps users out of screens they cannot act on. */
export function RequireRole({ roles, children }: { roles: UserRole[]; children: ReactNode }) {
  const profile = useAuthStore((s) => s.profile);
  const loading = useAuthStore((s) => s.loading);
  // hasRole, not profile.role: a branch manager is not a quarry manager.
  // 0055: a module an administrator gave this user opens its screens to
  // read; what they may change still follows the role (and the server).
  const location = useLocation();
  const module = moduleForPath(location.pathname);
  const allowed = useAuthStore((s) => s.hasRole(...roles)
    || (!!s.access?.custom_modules && !!s.profile && !s.profile.home_site_id
        && !!module && module !== 'administration' && s.hasModule(module)));

  if (loading) return <div className="centered"><span className="spinner" /></div>;
  if (!profile || !allowed) {
    return (
      <div className="placeholder">
        <h2>Not available for your role</h2>
        <p className="muted">
          This screen is limited to: {roles.join(', ')}. Your role is {profile?.role ?? 'unknown'}.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

/** The quarry screens. A branch user has none of them (their data is not
 *  readable to them at all, 0035), so they land on the concrete screens. */
export function QuarryOnly() {
  const branch = useAuthStore((s) => s.profile?.home_site_id);
  if (branch) return <Navigate to="/concrete" replace />;
  return <Outlet />;
}

/** The concrete screens: quarry management and branch users. */
export function RequireConcrete({ children }: { children: ReactNode }) {
  const profile = useAuthStore((s) => s.profile);
  const loading = useAuthStore((s) => s.loading);
  const memberBranches = useAuthStore((s) => s.memberBranches().length);
  if (loading) return <div className="centered"><span className="spinner" /></div>;
  const ok = !!profile && (memberBranches > 0 || profile.role === 'manager' || profile.role === 'admin');
  if (!ok) {
    return (
      <div className="placeholder">
        <h2>Not available for your role</h2>
        <p className="muted">The concrete screens are for the branches and for quarry management.</p>
      </div>
    );
  }
  return <>{children}</>;
}

/** Module gate for every screen (0055). The server refuses the data of a
 *  module the user does not have; this sends them to a screen they do have,
 *  or says why the page is closed. */
export function ModuleGate() {
  const location = useLocation();
  const loading = useAuthStore((s) => s.loading);
  const module = moduleForPath(location.pathname);
  const allowed = useAuthStore((s) => !module || s.hasModule(module));
  const isBranch = useAuthStore((s) => !!s.profile?.home_site_id);
  const hasModule = useAuthStore((s) => s.hasModule);
  useAuthStore((s) => s.access);   // re-check when access arrives

  if (loading) return <div className="centered"><span className="spinner" /></div>;
  if (!allowed) {
    const order = isBranch ? BRANCH_LANDING : QUARRY_LANDING;
    const landing = order.find(([m]) => hasModule(m))?.[1] ?? '/help';
    // A branch user is always taken to their own screens (as before 0055);
    // anyone arriving at the start page goes to their first module.
    if ((isBranch || location.pathname === '/') && landing !== location.pathname) {
      return <Navigate to={landing} replace />;
    }
    const label = MODULES.find((m) => m.key === module)?.label ?? 'This module';
    return (
      <div className="placeholder">
        <h2>{label} is not available to you</h2>
        <p className="muted">
          Your account does not include the {label} module. An administrator can add it under
          Administration → Users &amp; roles.
        </p>
      </div>
    );
  }
  return <Outlet />;
}

const QUARRY_LANDING: [ModuleKey, string][] = [
  ['dashboard', '/'], ['diary', '/diary'], ['fleet', '/machines'], ['production', '/production'],
  ['blasting', '/blasts'], ['breakdowns', '/breakdowns'], ['vehicle_checks', '/fleet/checks'],
  ['fuel', '/fuel'], ['workshop', '/workshop'], ['maintenance', '/maintenance'], ['parts', '/parts'],
  ['targets', '/targets'], ['reports', '/reports'], ['company', '/company'], ['concrete', '/concrete'],
  ['brick', '/brick'], ['precast', '/precast'], ['administration', '/admin'],
];

const BRANCH_LANDING: [ModuleKey, string][] = [
  ['diary', '/diary'],
  ['concrete', '/concrete'], ['brick', '/brick'], ['precast', '/precast'], ['fleet', '/fleet/branch'],
  ['vehicle_checks', '/fleet/checks'], ['breakdowns', '/fleet/breakdowns'], ['fuel', '/fuel'],
];
